/**
 * @TODO css custom properties (colors, font-sizes, line-heighs, box-shadows, etc)
 */

import React, {
  forwardRef,
  HTMLAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { styled } from '@linaria/react';
import PropTypes from 'prop-types';

import { Close } from '../icon';

const ModalLayout = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.25);
  z-index: 999;
`;

const ModalContainer = styled.div`
  background-color: #fff;
  position: relative;
  z-index: 1000;
  display: inline-block;
  padding: 16px;
  box-sizing: border-box;
  border-radius: 3px;
  box-shadow: 10px 10px 30px rgb(32 54 70 / 10%);
  margin: 8px 16px;

  @media (min-width: 1024px) {
    padding: 24px;
  }
`;

const ModalHeader = styled.div<{ flexEnd: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ flexEnd }) => (flexEnd ? 'flex-end' : 'space-between')};
  font-size: 20px;
  line-height: 26px;
  color: #424242;
  font-weight: bold;
  padding-bottom: 24px;
`;

const ModalClose = styled.button`
  outline: none;
  padding: 0;
  border: none;
  background-color: transparent;
  border-radius: 50%;
  margin-left: 16px;

  &:hover {
    cursor: pointer;
  }
`;

const ModalBody = styled.div``;

/**
 * @param {String} cssClassName
 */
const createPortalWrapper = (cssClassName: string) => {
  const wrapper = document.createElement('div');
  wrapper.classList.add(cssClassName);
  document.body.appendChild(wrapper);

  return wrapper;
};

export type ModalProps = {
  title: React.ReactNode;
  wrapperCssClass?: string;
  children: React.ReactNode;
  defaultOpened?: boolean;
  afterClose?: () => void;
  containerProps?: HTMLAttributes<HTMLDivElement>;
  disableScrollOnBodyWhenOpen?: boolean;
};

export interface ModalRef {
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

const Modal = forwardRef<ModalRef, ModalProps>(
  (
    {
      defaultOpened = false,
      title = '',
      wrapperCssClass = 'bb-modals',
      afterClose,
      disableScrollOnBodyWhenOpen = false,
      children,
      containerProps = {},
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(defaultOpened);
    const [wrapper, setWrapper] = useState<Element | null>(null);
    const close = useCallback(() => {
      setIsOpen(false);
      afterClose?.();
    }, []);
    const handleEscape = useCallback(
      (event: KeyboardEvent) => {
        if (event.keyCode === 27) close();
      },
      [close],
    );

    useImperativeHandle(
      ref,
      () => ({
        open: () => setIsOpen(true),
        close,
        isOpen,
      }),
      [close],
    );

    useLayoutEffect(() => {
      let element = document.querySelector(`.${wrapperCssClass}`);
      let created = false;

      if (!element) {
        element = createPortalWrapper(wrapperCssClass);
        created = true;
      }
      setWrapper(element);

      return () => {
        if (created && element.parentNode) {
          element.parentNode.removeChild(element);
        }
      };
    }, [wrapperCssClass]);

    useEffect(() => {
      if (isOpen) {
        document.addEventListener('keydown', handleEscape, false);
        disableScrollOnBodyWhenOpen && document.body.classList.add('bb-modal-open');
      } else {
        disableScrollOnBodyWhenOpen && document.body.classList.remove('bb-modal-open');
      }

      return () => {
        document.removeEventListener('keydown', handleEscape, false);
      };
    }, [isOpen]);

    if (!wrapper || !isOpen) return null;

    return createPortal(
      <ModalLayout>
        <ModalOverlay onClick={close} />
        <ModalContainer {...containerProps}>
          <ModalHeader flexEnd={!title}>
            {title}
            <ModalClose onClick={close}>
              <Close />
            </ModalClose>
          </ModalHeader>
          <ModalBody>{children}</ModalBody>
        </ModalContainer>
      </ModalLayout>,
      // TODO: refactor to avoid using DOM && create test
      document.querySelector(`.${wrapperCssClass}`) as Element,
    );
  },
);

Modal.propTypes = {
  title: PropTypes.node,
  children: PropTypes.node,
  defaultOpened: PropTypes.bool,
  afterClose: PropTypes.func,
  disableScrollOnBodyWhenOpen: PropTypes.bool,
  wrapperCssClass: PropTypes.string,
  containerProps: PropTypes.object,
};

export default Modal;
